import type { SceneObject } from "../SceneHost";
import { readObjectStyle } from "../../lib/board/sceneStyle";

export const FRAME_DEFAULT_TINT = "#94a3b8";

/** Пресеты оттенка рамки (заливка остаётся полупрозрачной). */
export const FRAME_TINT_PRESETS = [
  "#94a3b8", // нейтральный
  "#3b82f6", // синий
  "#22c55e", // зелёный
  "#a855f7", // фиолетовый
  "#f59e0b", // янтарный
  "#ef4444", // красный
  "#14b8a6", // бирюзовый
  "#ec4899", // розовый
] as const;

export function isFrameObject(
  obj: SceneObject | null | undefined
): obj is SceneObject & { type: "frame"; w: number; h: number } {
  return Boolean(obj) && obj!.type === "frame";
}

/** Зона клика по заголовку над рамкой (world) — подпись рисуется на y≈−8. */
export const FRAME_TITLE_HIT_H = 28;

/** AABB рамки + полоса заголовка сверху (для hit-test / spatial index). */
export function frameHitBounds(obj: SceneObject): { x: number; y: number; w: number; h: number } {
  const w = typeof obj.w === "number" ? obj.w : 0;
  const h = typeof obj.h === "number" ? obj.h : 0;
  return {
    x: obj.x,
    y: obj.y - FRAME_TITLE_HIT_H,
    w,
    h: h + FRAME_TITLE_HIT_H,
  };
}

/** Точка в полосе заголовка (не в теле рамки). */
export function pointInFrameTitleBand(obj: SceneObject, x: number, y: number): boolean {
  if (!isFrameObject(obj)) return false;
  const w = typeof obj.w === "number" ? obj.w : 0;
  return x >= obj.x && x <= obj.x + w && y >= obj.y - FRAME_TITLE_HIT_H && y < obj.y;
}

export function frameTitle(obj: SceneObject): string {
  return typeof obj.title === "string" && obj.title.trim() ? obj.title.trim() : "Рамка";
}

function parseTintHex(raw: string | undefined): string | null {
  if (!raw || raw === "none") return null;
  const m = /^#?([0-9a-f]{6})$/i.exec(raw.trim());
  if (m) return `#${m[1]!.toLowerCase()}`;
  const rgba = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(raw);
  if (rgba) {
    const to = (v: string) => Number(v).toString(16).padStart(2, "0");
    return `#${to(rgba[1]!)}${to(rgba[2]!)}${to(rgba[3]!)}`;
  }
  return null;
}

/** Базовый цвет рамки (hex); прозрачность задаётся при отрисовке. */
export function frameTintColor(obj: SceneObject): string {
  const style = readObjectStyle(obj);
  return (
    parseTintHex(typeof style.fill === "string" ? style.fill : undefined) ||
    parseTintHex(typeof style.borderColor === "string" ? style.borderColor : undefined) ||
    FRAME_DEFAULT_TINT
  );
}

export function framePaint(obj: SceneObject): { fill: string; stroke: string; title: string } {
  const hex = frameTintColor(obj);
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  const n = m ? parseInt(m[1]!, 16) : 0x94a3b8;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return {
    fill: `rgba(${r},${g},${b},0.12)`,
    stroke: hex,
    title: hex,
  };
}

/** Верхняя (по z/array) рамка, в AABB которой лежит точка. */
export function frameAtPoint(
  objects: SceneObject[],
  x: number,
  y: number
): SceneObject | null {
  for (let i = objects.length - 1; i >= 0; i--) {
    const obj = objects[i]!;
    if (!isFrameObject(obj)) continue;
    const w = typeof obj.w === "number" ? obj.w : 0;
    const h = typeof obj.h === "number" ? obj.h : 0;
    if (x >= obj.x && x <= obj.x + w && y >= obj.y && y <= obj.y + h) {
      return obj;
    }
  }
  return null;
}

export function applyFrameContext(obj: SceneObject, frame: SceneObject | null): void {
  if (!frame) {
    delete obj.frameId;
    return;
  }
  obj.frameId = frame.id;
}

/** Центр объекта (для AABB-объектов; path/line — bbox center через x+w/2). */
export function objectCenter(obj: SceneObject): { x: number; y: number } {
  const w = typeof obj.w === "number" ? obj.w : 0;
  const h = typeof obj.h === "number" ? obj.h : 0;
  return { x: obj.x + w / 2, y: obj.y + h / 2 };
}

export function collectFrameChildIds(objects: SceneObject[], frameId: string): string[] {
  return objects.filter((o) => o.frameId === frameId && o.id !== frameId).map((o) => o.id);
}

export function computeFrameMovePatches(
  objects: SceneObject[],
  frameId: string,
  dx: number,
  dy: number,
  excludeIds?: Set<string>
): { id: string; patch: { x: number; y: number } }[] {
  if (!dx && !dy) return [];
  return objects
    .filter(
      (o) =>
        o.frameId === frameId &&
        o.id !== frameId &&
        !excludeIds?.has(o.id)
    )
    .map((o) => ({
      id: o.id,
      patch: { x: Math.round(o.x + dx), y: Math.round(o.y + dy) },
    }));
}

/** Патч frameId по центру объекта после переноса (null = снять привязку). */
/** Рамки сцены в исходном порядке — считается один раз на пакет правок. */
export function collectFrames(objects: SceneObject[]): SceneObject[] {
  return objects.filter((o) => isFrameObject(o));
}

export function computeFrameReparentPatch(
  objects: SceneObject[],
  obj: SceneObject,
  /** Готовый список рамок: иначе на каждый перенесённый объект шёл проход по всей сцене. */
  framesPrecomputed?: SceneObject[]
): { frameId: string | null } | null {
  if (isFrameObject(obj)) return null;
  const c = objectCenter(obj);
  // obj заведомо не рамка, поэтому исключать его из поиска не нужно.
  const frame = frameAtPoint(
    framesPrecomputed ?? objects.filter((o) => o.id !== obj.id),
    c.x,
    c.y
  );
  const nextId = frame ? frame.id : null;
  const prev =
    typeof obj.frameId === "string" && obj.frameId ? obj.frameId : null;
  if (nextId === prev) return null;
  return { frameId: nextId };
}
